(() => {
  /* BEZELOR spec sheet v2. Rebuilds the product page spec sheet from the specification table in the
     product description (the same source as before) with:
     - brand and model number on one line
     - key figure tiles (movement, sizes, strap, weight) that count up when they come into view
     - front and side view drawings in proportion (case size, case thickness, strap width)
     - a case size guide and a drawing of the clasp style
     - at least four feature cards with icons
     - the complete table, folded away under "Full specifications"
     Listings that put the case thickness under "Dial Diameter" (e.g. 41mm + 11mm) are shown correctly
     as case size and thickness. Nothing is invented: every value comes from the table. */
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const svg = (paths, cls) => '<svg class="' + (cls || '') + '" viewBox="0 0 24 24" aria-hidden="true">' + paths + '</svg>';
  const ICONS = {
    movement: '<circle cx="12" cy="13" r="7"/><path d="M12 9.5V13l2.5 1.6M9.5 3h5M12 3v3"/>',
    size: '<circle cx="12" cy="12" r="7"/><path d="M5 12h14M7.5 10 5 12l2.5 2M16.5 10 19 12l-2.5 2"/>',
    thickness: '<rect x="9" y="4" width="6" height="16" rx="2"/><path d="M4 8v8M20 8v8M4 12h3M17 12h3"/>',
    strap: '<path d="M8 2h8l-1 6H9L8 2ZM9 16h6l1 6H8l1-6Z"/><rect x="6" y="8" width="12" height="8" rx="3"/>',
    water: '<path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z"/>',
    crystal: '<path d="M12 3 4.5 9 7 20h10l2.5-11L12 3Z"/><path d="m8 9 4 11 4-11M4.5 9h15"/>',
    material: '<path d="M4 7h16v10H4z"/><path d="M8 7v10M16 7v10"/>',
    weight: '<path d="M6 9h12l2 11H4L6 9Z"/><circle cx="12" cy="6" r="2.5"/>',
    length: '<path d="M3 12h18M6 9l-3 3 3 3M18 9l3 3-3 3M9 10v4M12 10v4M15 10v4"/>',
    luminous: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"/><path d="M17 3v3M15.5 4.5h3"/>',
    calendar: '<rect x="4" y="5" width="16" height="15" rx="2"/><path d="M4 10h16M9 3v4M15 3v4M9 14h2"/>',
    chrono: '<circle cx="12" cy="13" r="7"/><path d="M12 13V9M10 3h4M18.5 6.5 20 5"/>',
    alarm: '<circle cx="12" cy="13" r="7"/><path d="M12 10v3l2 2M4 5l3-2M20 5l-3-2"/>',
    shield: '<path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-4"/>',
    gem: '<path d="M6 4h12l3 5-9 11L3 9l3-5Z"/><path d="M3 9h18M9 4l3 16 3-16"/>',
    rotor: '<circle cx="12" cy="12" r="8"/><path d="M12 12 6.5 6.5A8 8 0 0 1 17.5 6.5Z"/>',
    moon: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"/>',
    check: '<path d="m5 12 4.5 4.5L19 7"/>',
    badge: '<circle cx="12" cy="9" r="6"/><path d="m8.5 14-1.5 7 5-3 5 3-1.5-7"/>'
  };
  const featureIcon = (text) => {
    const t = text.toLowerCase();
    if (/lumin|glow|night/.test(t)) return 'luminous';
    if (/water|swim|dive/.test(t)) return 'water';
    if (/date|day|calendar|week|month/.test(t)) return 'calendar';
    if (/chrono|stopwatch|timer/.test(t)) return 'chrono';
    if (/alarm/.test(t)) return 'alarm';
    if (/moon/.test(t)) return 'moon';
    if (/shock|scratch/.test(t)) return 'shield';
    if (/diamond|rhinestone|crystal|gem/.test(t)) return 'gem';
    if (/auto|self.?wind|mechanical|skeleton/.test(t)) return 'rotor';
    return 'check';
  };
  const esc = (text) => String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  /* Millimetres from a value: "44mm", "40 to 44 mm" (the larger figure), or a bare "14" or "20-24" with no unit */
  const mmOf = (value) => {
    const text = String(value || '');
    const match = /(\d+(?:\.\d+)?)\s*mm/i.exec(text);
    if (match) return parseFloat(match[1]);
    const bare = /^\s*(?:\d+(?:\.\d+)?\s*(?:to|-|–)\s*)?(\d+(?:\.\d+)?)\s*$/i.exec(text);
    return bare ? parseFloat(bare[1]) : 0;
  };
  /* Drawing labels always show the unit */
  const mmLabel = (value) => (/mm/i.test(value) || !/^\s*[\d.\s\-–]+(?:to\s*[\d.\s]+)?$/i.test(value) ? value : value.trim() + ' mm');

  /* Butterfly deployant clasp (folding clasps). Two mirrored metal wings are hinged at the outer ends.
     Sequence: the push buttons press in (unlock), both wings swing up on their hinges and stand tilted toward
     the strap, revealing the dark channel and centre pins, then fold back down together and seat with a small click.
     The wings stay within the closed width, so the clasp never grows wider. Only transform is animated
     (GPU friendly); the box has a fixed aspect ratio, so nothing shifts. */
  const FOLD_CSS =
    '.bzs-clasp__art.has-bzf{display:grid;place-items:center}' +
    '.bzf-box{position:relative;width:min(100%,264px);aspect-ratio:240/100;contain:layout}' +
    '.bzf-box>span{position:absolute;display:block}' +
    '.bzf-link{top:30%;height:40%;border-radius:4px;background:rgba(255,255,255,.07);border:1.2px solid rgba(212,180,107,.5);box-sizing:border-box}' +
    '.bzs-mat-steel .bzf-link{background:#2c2d31;border-color:#8a7648}' +
    '.bzf-bed{left:23.4%;width:53.2%;top:31%;height:38%;border-radius:6px;background:linear-gradient(180deg,#07080b,#11141b);box-shadow:inset 0 0 0 1px rgba(212,180,107,.28),inset 0 3px 6px rgba(0,0,0,.7)}' +
    '.bzf-bed i{position:absolute;top:50%;width:5px;height:5px;margin-top:-2.5px;border-radius:50%;background:radial-gradient(circle at 35% 35%,#fff4cf,#b8913f);box-shadow:0 0 0 1px rgba(0,0,0,.5)}' +
    '.bzf-bed i:first-child{left:calc(50% - 9px)}.bzf-bed i:last-child{left:calc(50% + 4px)}' +
    '.bzf-btn{left:45%;width:10%;height:9%;border-radius:3px;background:linear-gradient(180deg,#fff4cf,#d4b46b 60%,#a7823a);will-change:transform}' +
    '.bzf-btn.is-t{top:19%}.bzf-btn.is-b{top:72%}' +
    '.bzf-scene{left:22.5%;width:55%;top:28%;height:44%;perspective:700px;transform-style:preserve-3d}' +
    '.bzf-wing{position:absolute;top:0;bottom:0;width:50%;transform-style:preserve-3d;will-change:transform}' +
    '.bzf-wing.is-l{left:0;transform-origin:0 50%}.bzf-wing.is-r{right:0;transform-origin:100% 50%}' +
    '.bzf-face,.bzf-back{position:absolute;inset:0;box-sizing:border-box;-webkit-backface-visibility:hidden;backface-visibility:hidden}' +
    '.bzf-face{background:linear-gradient(180deg,#363d4b 0%,#1d222d 46%,#141821 100%);border:1.8px solid #d4b46b;box-shadow:inset 0 1px 0 rgba(255,244,207,.28)}' +
    '.is-l>.bzf-face{border-radius:8px 1px 1px 8px;border-right-width:1px}.is-r>.bzf-face{border-radius:1px 8px 8px 1px;border-left-width:1px}' +
    '.bzf-face:after{content:"";position:absolute;top:50%;height:1.2px;margin-top:-.6px;background:rgba(243,220,149,.6)}' +
    '.is-l>.bzf-face:after{left:14%;right:0}.is-r>.bzf-face:after{left:0;right:14%}' +
    '.bzf-back{transform:rotateY(180deg);background:linear-gradient(180deg,#191c24,#0b0d12);border:1.4px solid rgba(212,180,107,.65)}' +
    '.is-l>.bzf-back{border-radius:1px 8px 8px 1px}.is-r>.bzf-back{border-radius:8px 1px 1px 8px}' +
    '.bzf-back:after{content:"";position:absolute;top:32%;bottom:32%;width:16%;border-radius:2px;background:linear-gradient(90deg,#a7823a,#f3dc95,#a7823a)}' +
    '.is-l>.bzf-back:after{left:5%}.is-r>.bzf-back:after{right:5%}' +
    '.bzf-edge{position:absolute;top:0;bottom:0;width:4px;background:linear-gradient(90deg,#8a7648,#f3dc95,#8a7648);border-radius:1px}' +
    '.is-l>.bzf-edge{right:0;transform-origin:100% 50%;transform:rotateY(90deg)}.is-r>.bzf-edge{left:0;transform-origin:0 50%;transform:rotateY(-90deg)}' +
    '.bzf-knuckle{top:31%;height:38%;width:2.4%;border-radius:4px;background:linear-gradient(90deg,#8a7648,#f3dc95 50%,#8a7648);box-shadow:0 0 0 1px rgba(0,0,0,.35)}' +
    '.bzf-knuckle.is-l{left:21%}.bzf-knuckle.is-r{left:77%}' +
    '.bzf-sweep{position:absolute;inset:0;overflow:hidden;border-radius:8px;pointer-events:none;transform:translateZ(1px)}' +
    '.bzf-sweep:before{content:"";position:absolute;top:0;bottom:0;left:0;width:22%;background:linear-gradient(100deg,transparent,rgba(255,244,207,.32),transparent);opacity:0;transform:translateX(-120%)}' +
    '.bzs-clasp.is-in .bzf-wing.is-l{animation:bzfL 3s .3s both}' +
    '.bzs-clasp.is-in .bzf-wing.is-r{animation:bzfR 3s .3s both}' +
    '.bzs-clasp.is-in .bzf-btn.is-t{animation:bzfBt 3s .3s both}' +
    '.bzs-clasp.is-in .bzf-btn.is-b{animation:bzfBb 3s .3s both}' +
    '.bzs-clasp.is-in .bzf-sweep:before{animation:bzfSw 3s .3s both}' +
    '@keyframes bzfL{0%,12%{transform:rotateY(0deg);animation-timing-function:cubic-bezier(.45,.05,.3,1)}31%{transform:rotateY(-72deg)}40%{transform:rotateY(-72deg);animation-timing-function:cubic-bezier(.55,0,.85,.25)}53%{transform:rotateY(1.5deg);animation-timing-function:ease-out}57%,100%{transform:rotateY(0deg)}}' +
    '@keyframes bzfR{0%,12%{transform:rotateY(0deg);animation-timing-function:cubic-bezier(.45,.05,.3,1)}31%{transform:rotateY(72deg)}40%{transform:rotateY(72deg);animation-timing-function:cubic-bezier(.55,0,.85,.25)}53%{transform:rotateY(-1.5deg);animation-timing-function:ease-out}57%,100%{transform:rotateY(0deg)}}' +
    '@keyframes bzfBt{0%,5%{transform:none}11%,52%{transform:translateY(3.5px)}57%{transform:translateY(-1px)}62%,100%{transform:none}}' +
    '@keyframes bzfBb{0%,5%{transform:none}11%,52%{transform:translateY(-3.5px)}57%{transform:translateY(1px)}62%,100%{transform:none}}' +
    '@keyframes bzfSw{0%,60%{opacity:0;transform:translateX(-120%)}64%{opacity:1}80%{opacity:1;transform:translateX(480%)}84%,100%{opacity:0;transform:translateX(480%)}}' +
    '@media (prefers-reduced-motion:reduce){.bzs-clasp .bzf-wing,.bzs-clasp .bzf-btn,.bzs-clasp .bzf-sweep:before{animation:none!important}}';
  const foldCss = () => {
    if (document.getElementById('bzf-css')) return;
    const style = document.createElement('style');
    style.id = 'bzf-css';
    style.textContent = FOLD_CSS;
    document.head.append(style);
  };
  const butterflyClasp = (buttons) => {
    const pct = (n, of) => ((n / of) * 100).toFixed(2) + '%';
    const link = (x, w) => '<span class="bzf-link" style="left:' + pct(x, 240) + ';width:' + pct(w, 240) + '"></span>';
    const wing = (side) => '<span class="bzf-wing is-' + side + '"><span class="bzf-edge"></span><span class="bzf-back"></span><span class="bzf-face"></span></span>';
    return '<div class="bzf-box" aria-hidden="true">' +
      link(4, 22) + link(28, 22) + link(190, 22) + link(214, 22) +
      '<span class="bzf-bed"><i></i><i></i></span>' +
      '<span class="bzf-btn is-t"></span><span class="bzf-btn is-b"></span>' +
      '<span class="bzf-scene">' + wing('l') + wing('r') + '<span class="bzf-sweep"></span></span>' +
      '<span class="bzf-knuckle is-l"></span><span class="bzf-knuckle is-r"></span>' +
      '</div>';
  };

  const build = (sheet) => {
    const source = sheet.closest('.bezelor-pdp-panel')?.querySelector('.bezelor-pdp-spec-content');
    if (!source || sheet.dataset.v2) return;
    const rows = [];
    const seen = new Set();
    source.querySelectorAll('tr').forEach((row) => {
      const cells = row.querySelectorAll('th,td');
      if (cells.length < 2) return;
      const label = cells[0].textContent.replace(/\s+/g, ' ').trim();
      const value = cells[1].textContent.replace(/\s+/g, ' ').trim();
      const key = label.toLowerCase();
      if (!label || !value || key === 'specification' || key === 'specifications' || seen.has(key)) return;
      seen.add(key);
      rows.push({ label, value, key });
    });
    if (rows.length < 3) return;
    sheet.dataset.v2 = 'true';
    sheet.dataset.built = 'true'; /* the section's own builder skips a sheet that is already built */

    const used = new Set();
    const find = (...words) => rows.find((row) => !used.has(row) && words.some((word) => row.key.includes(word)));
    const take = (row) => {
      if (row) used.add(row);
      return row;
    };

    /* Sizes: the largest size row is the case; a much smaller "diameter" row is really the thickness */
    const sizeRows = rows.filter((row) => /diameter|dial size|case size|watch size/.test(row.key) && mmOf(row.value) > 0).sort((a, b) => mmOf(b.value) - mmOf(a.value));
    const caseRow = take(sizeRows[0]);
    let thickRow = take(find('thickness'));
    if (!thickRow && sizeRows.length > 1) {
      const small = sizeRows[sizeRows.length - 1];
      if (mmOf(small.value) < 18 && mmOf(caseRow.value) >= 20) thickRow = take(small);
    }
    sizeRows.slice(1).forEach((row) => {
      if (row !== thickRow && Math.abs(mmOf(row.value) - mmOf(caseRow.value)) < 0.01) take(row);
    });
    const strapRow = take(find('band width', 'strap width', 'lug width', 'bracelet width'));
    const lengthRow = take(find('strap length', 'band length', 'bracelet length'));
    const movementRow = take(find('movement'));
    const modelRow = take(find('model number', 'model'));
    const featureRow = take(find('feature', 'function'));
    const claspRow = take(find('clasp', 'buckle', 'closure'));
    const weightRow = take(find('weight'));
    const waterRow = find('water resistance', 'waterproof');
    const crystalRow = find('window material', 'mirror', 'crystal', 'glass');
    const materialRow = find('case material', 'material');
    const certRow = find('certif');

    const diameter = mmOf(caseRow?.value);
    const thickness = mmOf(thickRow?.value);
    const strapWidth = mmOf(strapRow?.value);

    sheet.replaceChildren();
    sheet.classList.add('is-v2');

    /* ---------- Head: brand and model on one line ---------- */
    const head = el('div', 'bz-spec__head');
    const eyebrow = el('p', 'bz-spec__eyebrow');
    eyebrow.append(el('span', '', 'BEZELOR specifications'));
    const ident = el('p', 'bzs-ident' + ((sheet.dataset.brand || '').length + (modelRow ? modelRow.value.length : 0) > 12 ? ' is-long' : ''));
    ident.append(el('span', 'bzs-ident__brand', sheet.dataset.brand || ''));
    if (modelRow) {
      ident.append(el('span', 'bzs-ident__sep'));
      const model = el('span', 'bzs-ident__model', modelRow.value);
      model.setAttribute('aria-label', 'Model ' + modelRow.value);
      ident.append(model);
    }
    head.append(eyebrow, ident);
    sheet.append(head);

    /* ---------- Key figures ---------- */
    const tiles = [
      /* two columns on phones: Movement | Weight, Case size | Case thickness, Strap width | Strap length
         (three columns on desktop are arranged in the stylesheet) */
      movementRow && { row: movementRow, label: 'Movement', icon: 'movement' },
      weightRow && { row: weightRow, label: 'Weight', icon: 'weight' },
      caseRow && { row: caseRow, label: 'Case size', icon: 'size' },
      thickRow && { row: thickRow, label: 'Case thickness', icon: 'thickness' },
      strapRow && { row: strapRow, label: 'Strap width', icon: 'strap' },
      lengthRow && { row: lengthRow, label: 'Strap length', icon: 'length' },
      waterRow && { row: waterRow, label: 'Water resistance', icon: 'water' },
      crystalRow && { row: crystalRow, label: 'Crystal', icon: 'crystal' }
    ].filter(Boolean).slice(0, 6);
    if (tiles.length) {
      const grid = el('div', 'bz-spec__keys bzs-reveal');
      tiles.forEach((tile, index) => {
        const box = el('div', 'bz-spec__key is-' + tile.icon);
        box.style.setProperty('--i', index);
        const mark = el('span', 'bz-spec__key-icon');
        mark.innerHTML = svg(ICONS[tile.icon]);
        const value = el('strong');
        const tileText = ['thickness', 'strap', 'size'].includes(tile.icon) ? mmLabel(tile.row.value) : tile.row.value;
        const isRange = /^\s*\d+(?:\.\d+)?\s*(?:to|-|–)\s*\d/i.test(tileText);
        const parts = isRange ? null : /^(\d+(?:\.\d+)?)\s*([a-z]+)?\s*(.*)$/i.exec(tileText);
        if (parts) {
          const n = el('span', 'bzs-count', parts[1]);
          n.dataset.to = parts[1];
          value.append(n, document.createTextNode(parts[2] || ''));
        } else value.textContent = tileText;
        box.append(mark, value);
        const extra = parts && parts[3] ? parts[3].replace(/^\((.*)\)$/, '$1') : '';
        box.append(el('small', '', tile.label + (extra ? ' · ' + extra : '')));
        grid.append(box);
      });
      sheet.append(grid);
    }

    /* ---------- Dimensions drawing (in proportion) and size guide ---------- */
    if (diameter >= 15 && diameter <= 60) {
      const card = el('section', 'bzs-dims bzs-reveal');
      const top = el('div', 'bzs-card-head');
      top.append(el('h3', 'bzs-card-title', 'Dimensions'), el('span', 'bzs-card-note', 'Shown in proportion'));
      card.append(top);
      const scale = 100 / diameter; /* px per mm: the case is 100px across */
      const cx = 80;
      const cy = 104;
      const r = 50;
      const sw = strapWidth ? Math.min(strapWidth * scale, r * 1.6) : r * 0.9;
      const th = thickness ? Math.max(thickness * scale, 8) : 0;
      const f = (n) => n.toFixed(1);
      const ticks = Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        const r1 = r * 0.78;
        const r2 = r * (i % 3 === 0 ? 0.62 : 0.7);
        return '<line x1="' + f(cx + r1 * Math.sin(a)) + '" y1="' + f(cy - r1 * Math.cos(a)) + '" x2="' + f(cx + r2 * Math.sin(a)) + '" y2="' + f(cy - r2 * Math.cos(a)) + '"/>';
      }).join('');
      const labels = [];
      let drawing =
        '<svg class="bzs-draw" viewBox="0 0 340 228" role="img" aria-label="' + esc('Watch dimensions. Front view: case size ' + caseRow.value + (strapRow ? ', strap width ' + strapRow.value : '') + '.' + (thickRow ? ' Side view: case thickness ' + thickRow.value + '.' : '')) + '">' +
        /* front view */
        '<rect class="bzs-strap" x="' + f(cx - sw / 2) + '" y="6" width="' + f(sw) + '" height="' + (cy - 6) + '" rx="6"/>' +
        '<rect class="bzs-strap" x="' + f(cx - sw / 2) + '" y="' + cy + '" width="' + f(sw) + '" height="' + (200 - cy) + '" rx="6"/>' +
        '<circle class="bzs-case" cx="' + cx + '" cy="' + cy + '" r="' + r + '"/>' +
        '<circle class="bzs-dial" cx="' + cx + '" cy="' + cy + '" r="' + f(r * 0.84) + '"/>' +
        '<g class="bzs-ticks">' + ticks + '</g>' +
        '<line class="bzs-hand" x1="' + cx + '" y1="' + cy + '" x2="' + cx + '" y2="' + f(cy - r * 0.5) + '"/>' +
        '<line class="bzs-hand" x1="' + cx + '" y1="' + cy + '" x2="' + f(cx + r * 0.38) + '" y2="' + f(cy + r * 0.12) + '"/>' +
        '<rect class="bzs-crown" x="' + (cx + r - 1) + '" y="' + (cy - 5) + '" width="7" height="10" rx="2"/>' +
        '<g class="bzs-dim"><path class="bzs-line" d="M' + (cx - r) + ' ' + (cy + r + 14) + 'H' + (cx + r) + '"/>' +
        '<path class="bzs-tick" d="M' + (cx - r) + ' ' + (cy + r + 8) + 'v12M' + (cx + r) + ' ' + (cy + r + 8) + 'v12"/></g>' +
        '<text class="bzs-caption" x="' + cx + '" y="222">Front · size</text>';
      labels.push({ x: cx, y: cy + r + 14, text: mmLabel(caseRow.value) });
      if (strapRow) {
        drawing +=
          '<g class="bzs-dim"><path class="bzs-line" d="M' + f(cx - sw / 2) + ' 24H' + f(cx + sw / 2) + '"/>' +
          '<path class="bzs-tick" d="M' + f(cx - sw / 2) + ' 18v12M' + f(cx + sw / 2) + ' 18v12"/></g>';
        labels.push({ x: cx, y: 24, text: mmLabel(strapRow.value) });
      }
      if (thickRow) {
        /* side view: the watch seen edge-on, crystal on top, lugs and strap curving down to the wrist */
        const sx = 186;
        const top = cy - th / 2;
        const bottom = cy + th / 2;
        const right = sx + 2 * r;
        /* profile of a round watch seen from the side: domed crystal, sloped bezel, case middle with the crown
           facing you, lugs curving down into the strap, shallow domed caseback. Heights follow the real thickness. */
        const ex = sx + r;
        const D = 2 * r;
        const y1 = top + th * 0.16;
        const y2 = top + th * 0.36;
        const y3 = top + th * 0.8;
        const lx = sx - 15;
        const rx2 = right + 15;
        const lugY = y3 + Math.max(5, th * 0.18);
        const midH = y3 - y2;
        const crH = Math.min(midH + 3, 12);
        const knurl = [-3, 0, 3].map((d) => 'M' + f(ex + d) + ' ' + f((y2 + y3) / 2 - crH / 2 + 2) + 'V' + f((y2 + y3) / 2 + crH / 2 - 2)).join('');
        drawing +=
          '<path class="bzs-band" d="M' + f(lx + 4) + ' ' + f(lugY - 1) + 'C' + f(lx - 4) + ' ' + f(lugY + 14) + ' ' + f(lx - 7) + ' ' + f(lugY + 34) + ' ' + f(lx - 6) + ' ' + f(lugY + 58) + '"/>' +
          '<path class="bzs-band" d="M' + f(rx2 - 4) + ' ' + f(lugY - 1) + 'C' + f(rx2 + 4) + ' ' + f(lugY + 14) + ' ' + f(rx2 + 7) + ' ' + f(lugY + 34) + ' ' + f(rx2 + 6) + ' ' + f(lugY + 58) + '"/>' +
          /* lugs */
          '<path class="bzs-case" d="M' + f(sx + 2) + ' ' + f(y2 + 1) + 'C' + f(sx - 8) + ' ' + f(y2 + 1) + ' ' + f(lx - 1) + ' ' + f(y3 - 4) + ' ' + f(lx) + ' ' + f(lugY) + 'H' + f(lx + 9) + 'C' + f(lx + 10) + ' ' + f(y3) + ' ' + f(sx - 2) + ' ' + f(y3) + ' ' + f(sx + 2) + ' ' + f(y3) + 'Z"/>' +
          '<path class="bzs-case" d="M' + f(right - 2) + ' ' + f(y2 + 1) + 'C' + f(right + 8) + ' ' + f(y2 + 1) + ' ' + f(rx2 + 1) + ' ' + f(y3 - 4) + ' ' + f(rx2) + ' ' + f(lugY) + 'H' + f(rx2 - 9) + 'C' + f(rx2 - 10) + ' ' + f(y3) + ' ' + f(right + 2) + ' ' + f(y3) + ' ' + f(right - 2) + ' ' + f(y3) + 'Z"/>' +
          /* caseback, case middle, bezel, crystal */
          '<path class="bzs-case" d="M' + f(ex - D * 0.42) + ' ' + f(y3 - 1) + 'C' + f(ex - D * 0.36) + ' ' + f(bottom + th * 0.06) + ' ' + f(ex + D * 0.36) + ' ' + f(bottom + th * 0.06) + ' ' + f(ex + D * 0.42) + ' ' + f(y3 - 1) + 'Z"/>' +
          '<rect class="bzs-case" x="' + sx + '" y="' + f(y2) + '" width="' + D + '" height="' + f(midH) + '" rx="' + f(Math.min(4, midH / 2)) + '"/>' +
          '<path class="bzs-case" d="M' + f(ex - D * 0.47) + ' ' + f(y2) + 'L' + f(ex - D * 0.41) + ' ' + f(y1) + 'H' + f(ex + D * 0.41) + 'L' + f(ex + D * 0.47) + ' ' + f(y2) + 'Z"/>' +
          '<path class="bzs-glass" d="M' + f(ex - D * 0.39) + ' ' + f(y1) + 'Q' + ex + ' ' + f(top - (y1 - top)) + ' ' + f(ex + D * 0.39) + ' ' + f(y1) + 'Z"/>' +
          /* crown, seen end-on */
          '<rect class="bzs-crown" x="' + f(ex - 6) + '" y="' + f((y2 + y3) / 2 - crH / 2) + '" width="12" height="' + f(crH) + '" rx="2.5"/>' +
          '<path d="' + knurl + '" stroke="#8a7648" stroke-width="1" fill="none"/>' +
          '<g class="bzs-dim"><path class="bzs-line" d="M' + (right + 26) + ' ' + f(top) + 'V' + f(bottom) + '"/>' +
          '<path class="bzs-tick" d="M' + (right + 20) + ' ' + f(top) + 'h12M' + (right + 20) + ' ' + f(bottom) + 'h12"/></g>' +
          '<text class="bzs-caption" x="' + (sx + r) + '" y="222">Side · thickness</text>';
        labels.push({ x: right + 26, y: cy - th / 2 - 18, text: mmLabel(thickRow.value) });
      }
      drawing += labels.map((label) => {
        const w = label.text.length * 7 + 16;
        return '<g class="bzs-label"><rect x="' + f(label.x - w / 2) + '" y="' + f(label.y - 10) + '" width="' + w + '" height="20" rx="10"/><text x="' + f(label.x) + '" y="' + f(label.y + 4) + '">' + esc(label.text) + '</text></g>';
      }).join('');
      drawing += '</svg>';
      const figure = el('div', 'bzs-figure');
      figure.innerHTML = drawing;
      card.append(figure);

      /* General case size guide */
      const bands = [
        { name: 'Small', from: 24, to: 34 },
        { name: 'Medium', from: 34, to: 40 },
        { name: 'Large', from: 40, to: 44 },
        { name: 'Extra large', from: 44, to: 50 }
      ];
      const pos = Math.max(0, Math.min(100, ((diameter - 24) / 26) * 100));
      const band = bands.find((b) => diameter < b.to) || bands[bands.length - 1];
      const guide = el('div', 'bzs-guide');
      const guideHead = el('p', 'bzs-guide__head');
      guideHead.append(el('span', '', 'Case size guide'), el('strong', '', band.name + ' · ' + caseRow.value));
      const track = el('div', 'bzs-guide__track');
      bands.forEach((b) => {
        const seg = el('span', 'bzs-guide__seg' + (b === band ? ' is-on' : ''), b.name);
        seg.style.flexGrow = String(b.to - b.from);
        track.append(seg);
      });
      const marker = el('span', 'bzs-guide__marker');
      marker.style.setProperty('--pos', pos.toFixed(1) + '%');
      track.append(marker);
      const scaleRow = el('div', 'bzs-guide__scale');
      [24, 34, 40, 44, 50].forEach((mm, i, all) => {
        const tick = el('span', '', i === 0 || i === all.length - 1 ? mm + 'mm' : String(mm));
        tick.style.left = (((mm - 24) / 26) * 100).toFixed(1) + '%';
        scaleRow.append(tick);
      });
      guide.append(guideHead, track, scaleRow, el('p', 'bzs-guide__note', 'A general guide: smaller cases sit closer to the wrist, larger cases make more of a statement.'));
      card.append(guide);
      sheet.append(card);
    }

    /* ---------- Weight: the watch is set on a scale and the reading counts up ---------- */
    const weightParts = weightRow ? /(\d+(?:\.\d+)?)\s*(kg|kilograms?|g|grams?|oz|ounces?|lbs?|pounds?)?\b/i.exec(weightRow.value) : null;
    if (weightParts && parseFloat(weightParts[1]) > 0) {
      const amount = parseFloat(weightParts[1]);
      const unitRaw = (weightParts[2] || 'g').toLowerCase();
      const unit = /^k/.test(unitRaw) ? 'kg' : /^(oz|ounce)/.test(unitRaw) ? 'oz' : /^(lb|pound)/.test(unitRaw) ? 'lb' : 'g';
      const grams = amount * { g: 1, kg: 1000, oz: 28.35, lb: 453.6 }[unit];
      const feel =
        grams < 60 ? { name: 'Featherlight', text: 'Barely noticeable on the wrist, so it is easy to wear all day.' }
          : grams < 110 ? { name: 'Light', text: 'Light and comfortable for all-day wear, with enough weight to feel well made.' }
            : grams < 160 ? { name: 'Balanced', text: 'A reassuring weight you notice on the wrist without it feeling heavy.' }
              : { name: 'Substantial', text: 'A solid, weighty feel with real presence on the wrist.' };
      const wx = 120;
      const wy = 60;
      const wr = 25;
      const f = (n) => n.toFixed(1);
      const wticks = Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        const r1 = wr * 0.76;
        const r2 = wr * (i % 3 === 0 ? 0.58 : 0.66);
        return '<line x1="' + f(wx + r1 * Math.sin(a)) + '" y1="' + f(wy - r1 * Math.cos(a)) + '" x2="' + f(wx + r2 * Math.sin(a)) + '" y2="' + f(wy - r2 * Math.cos(a)) + '"/>';
      }).join('');
      const card = el('section', 'bzs-weight bzs-reveal');
      const top = el('div', 'bzs-card-head');
      top.append(el('h3', 'bzs-card-title', 'Weight'), el('span', 'bzs-card-note', 'Weighed on the scale'));
      card.append(top);
      const body = el('div', 'bzs-clasp__body');
      const art = el('div', 'bzs-clasp__art bzs-weight__art');
      art.innerHTML =
        '<svg viewBox="0 0 240 150" role="img" aria-label="' + esc('Watch on a scale reading ' + weightRow.value) + '">' +
        /* scale body, feet and display */
        '<rect class="bzs-scale-foot" x="44" y="138" width="18" height="6" rx="2"/><rect class="bzs-scale-foot" x="178" y="138" width="18" height="6" rx="2"/>' +
        '<rect class="bzs-scale-body" x="30" y="108" width="180" height="32" rx="10"/>' +
        '<rect class="bzs-lcd" x="80" y="115" width="80" height="19" rx="5"/>' +
        '<text class="bzs-lcd-text" x="152" y="129"><tspan class="bzs-wnum" data-to="' + weightParts[1] + '" data-delay="1100" data-dur="1300">' + (0).toFixed((weightParts[1].split('.')[1] || '').length) + '</tspan><tspan class="bzs-lcd-unit" dx="2">' + unit + '</tspan></text>' +
        '<circle class="bzs-lcd-dot" cx="89" cy="124.5" r="2"/>' +
        /* the pan and the watch press down together when the watch lands */
        '<g class="bzs-load">' +
        '<rect class="bzs-scale-pan" x="40" y="100" width="160" height="9" rx="4.5"/>' +
        '<ellipse class="bzs-wshadow" cx="' + wx + '" cy="100" rx="30" ry="3"/>' +
        '<g class="bzs-wwatch">' +
        '<path class="bzs-strap" d="M' + (wx - 12) + ' 100V' + wy + 'h24V100z"/>' +
        '<path class="bzs-strap" d="M' + (wx - 12) + ' ' + wy + 'V22a12 12 0 0 1 24 0V' + wy + 'z"/>' +
        '<circle class="bzs-case" cx="' + wx + '" cy="' + wy + '" r="' + wr + '"/>' +
        '<circle class="bzs-dial" cx="' + wx + '" cy="' + wy + '" r="' + f(wr * 0.84) + '"/>' +
        '<g class="bzs-ticks">' + wticks + '</g>' +
        '<line class="bzs-hand" x1="' + wx + '" y1="' + wy + '" x2="' + wx + '" y2="' + f(wy - wr * 0.5) + '"/>' +
        '<line class="bzs-hand" x1="' + wx + '" y1="' + wy + '" x2="' + f(wx + wr * 0.38) + '" y2="' + f(wy + wr * 0.12) + '"/>' +
        '<rect class="bzs-crown" x="' + (wx + wr - 1) + '" y="' + (wy - 4) + '" width="6" height="8" rx="2"/>' +
        '</g></g></svg>';
      const copy = el('div', 'bzs-clasp__copy');
      const heading = el('strong', '', feel.name + ' · ' + weightRow.value);
      copy.append(heading, el('p', '', feel.text), el('p', 'bzs-guide__note', 'A general guide: how a watch feels also depends on the strap fit.'));
      body.append(art, copy);
      card.append(body);
      sheet.append(card);
    }

    /* ---------- Clasp: drawing of the clasp style ---------- */
    if (claspRow) {
      const type = claspRow.key + ' ' + claspRow.value.toLowerCase();
      const kind = /magnet/.test(type) ? 'magnetic' : /hook/.test(type) ? 'hook' : /pin|tang|buckle/.test(type) && !/fold|butterfly|deploy|push|hidden/.test(type) ? 'buckle' : 'folding';
      const buttons = /push|button/.test(type);
      const butterfly = /butterfly/.test(type);
      const text = {
        folding: butterfly
          ? 'Two hinged halves fold together under the wrist, so the bracelet closes neatly with no visible buckle.'
          : buttons
            ? 'Folds flat under the bracelet for a clean, seamless look. Press the buttons on each side to open it.'
            : 'Folds closed under the wrist, keeping the bracelet secure and easy to put on.',
        buckle: 'A classic buckle: the pin goes through the strap holes, so you choose the fit.',
        magnetic: 'Closes with a magnet: slide it to the size you want and it holds in place.',
        hook: 'A hook buckle that latches into the strap for an adjustable fit.'
      }[kind];
      const drawings = {
        folding:
          '<rect class="bzs-link" x="4" y="30" width="22" height="40" rx="4"/><rect class="bzs-link" x="28" y="30" width="22" height="40" rx="4"/>' +
          '<rect class="bzs-link" x="190" y="30" width="22" height="40" rx="4"/><rect class="bzs-link" x="214" y="30" width="22" height="40" rx="4"/>' +
          '<rect class="bzs-plate" x="54" y="28" width="132" height="44" rx="8"/>' +
          '<path class="bzs-fold" d="M66 50h108"/><path class="bzs-hinge" d="M120 32v36"/>' +
          (buttons ? '<rect class="bzs-btn" x="108" y="18" width="24" height="10" rx="4"/><rect class="bzs-btn" x="108" y="72" width="24" height="10" rx="4"/>' : '') +
          (butterfly ? '<path class="bzs-fold" d="M66 36 120 50 66 64M174 36 120 50 174 64"/>' : ''),
        buckle:
          '<rect class="bzs-link" x="4" y="36" width="120" height="28" rx="8"/>' +
          '<circle class="bzs-hole" cx="30" cy="50" r="3"/><circle class="bzs-hole" cx="48" cy="50" r="3"/><circle class="bzs-hole" cx="66" cy="50" r="3"/>' +
          '<rect class="bzs-plate is-frame" x="120" y="26" width="34" height="48" rx="9"/><path class="bzs-hinge" d="M120 50h30"/>' +
          '<rect class="bzs-link" x="150" y="36" width="86" height="28" rx="8"/>',
        magnetic:
          '<rect class="bzs-link is-mesh" x="4" y="34" width="170" height="32" rx="6"/>' +
          '<rect class="bzs-plate" x="170" y="28" width="30" height="44" rx="6"/><path class="bzs-fold" d="M178 40h14M178 50h14M178 60h14"/>' +
          '<rect class="bzs-link is-mesh" x="196" y="34" width="40" height="32" rx="6"/>',
        hook:
          '<rect class="bzs-link is-mesh" x="4" y="34" width="150" height="32" rx="6"/>' +
          '<path class="bzs-plate is-frame" d="M150 30h40a14 14 0 0 1 0 28h-24"/>' +
          '<rect class="bzs-link is-mesh" x="176" y="44" width="60" height="22" rx="6"/>'
      };
      const card = el('section', 'bzs-clasp bzs-reveal');
      card.append(el('h3', 'bzs-card-title', 'Clasp'));
      const body = el('div', 'bzs-clasp__body');
      const art = el('div', 'bzs-clasp__art');
      if (kind === 'folding') {
        foldCss();
        art.classList.add('has-bzf');
        art.innerHTML = butterflyClasp(buttons);
      } else art.innerHTML = '<svg viewBox="0 0 240 100" aria-hidden="true">' + drawings[kind] + '</svg>';
      const copy = el('div', 'bzs-clasp__copy');
      copy.append(el('strong', '', claspRow.value), el('p', '', text));
      body.append(art, copy);
      card.append(body);
      sheet.append(card);
    }

    /* ---------- Features (at least four, all from this product's table) ---------- */
    const parts = featureRow ? featureRow.value.split(/,|\/|;|\s\|\s/).map((part) => part.trim()).filter(Boolean) : [];
    const extras = [
      materialRow && { text: materialRow.value, icon: 'material' },
      crystalRow && { text: /crystal|glass|mirror/i.test(crystalRow.value) ? crystalRow.value : crystalRow.value + ' crystal', icon: 'crystal' },
      waterRow && { text: 'Water resistant · ' + waterRow.value, icon: 'water' },
      certRow && { text: certRow.value + ' certified', icon: 'badge' }
    ].filter(Boolean);
    const items = parts.map((part) => ({ text: part.replace(/^\w/, (c) => c.toUpperCase()), icon: featureIcon(part) }));
    const target = Math.max(4, items.length + (items.length % 2));
    while (items.length < target && extras.length) items.push(extras.shift());
    if (items.length) {
      const box = el('section', 'bzs-features bzs-reveal');
      box.append(el('h3', 'bzs-card-title', 'Features'));
      const list = el('ul', 'bzs-features__list');
      items.forEach((item, index) => {
        const li = el('li');
        li.style.setProperty('--i', index);
        const mark = el('span', 'bzs-features__icon');
        mark.innerHTML = svg(ICONS[item.icon] || ICONS.check);
        li.append(mark, el('span', '', item.text));
        list.append(li);
      });
      box.append(list);
      sheet.append(box);
    }

    /* ---------- Full specifications: the complete table, folded away ---------- */
    {
      const list = el('dl', 'bz-spec__list');
      rows.forEach((row) => {
        const line = el('div', 'bz-spec__row');
        const label = row === thickRow && !/thick/.test(row.key) ? 'Case thickness' : row === caseRow && /diameter|dial size/.test(row.key) ? 'Case size' : row.label;
        line.append(el('dt', '', label), el('dd', '', row.value));
        list.append(line);
      });
      const wrap = el('div', 'bzs-details');
      wrap.id = sheet.closest('.bezelor-pdp-panel').id + '-details';
      wrap.append(list);
      const more = el('button', 'bz-spec__more');
      more.type = 'button';
      more.setAttribute('aria-expanded', 'false');
      more.setAttribute('aria-controls', wrap.id);
      const closedText = 'Full specifications (' + rows.length + ')';
      more.innerHTML = '<span></span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
      more.querySelector('span').textContent = closedText;
      more.addEventListener('click', () => {
        const open = sheet.classList.toggle('is-open');
        more.setAttribute('aria-expanded', String(open));
        more.querySelector('span').textContent = open ? 'Hide full specifications' : closedText;
        if (open && !reduce.matches && wrap.animate) {
          wrap.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 280, easing: 'ease-out' });
        }
      });
      sheet.append(more, wrap);
    }

    const note = [...source.querySelectorAll('p, span, div')].map((node) => node.textContent.trim()).find((text) => /^note/i.test(text) && text.length < 280);
    sheet.append(el('p', 'bz-spec__note', note || 'Measurements are approximate and supplied by the manufacturer.'));
    sheet.hidden = false;
    source.classList.add('bz-spec-source');
    const panelHeading = sheet.closest('.bezelor-pdp-panel')?.querySelector('.bezelor-pdp-panel__heading');
    if (panelHeading) panelHeading.style.display = 'none';

    /* ---------- Motion: reveal, count up, draw ---------- */
    const countUp = (node) => {
      const to = parseFloat(node.dataset.to);
      const decimals = (node.dataset.to.split('.')[1] || '').length;
      const done = () => {
        node.textContent = node.dataset.to;
        node.closest('.bzs-weight')?.classList.add('is-done');
      };
      if (reduce.matches || !to) return done();
      const duration = Number(node.dataset.dur) || 900;
      node.textContent = (0).toFixed(decimals);
      setTimeout(() => {
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          node.textContent = (to * eased).toFixed(decimals);
          if (t < 1) requestAnimationFrame(step);
          else done();
        };
        requestAnimationFrame(step);
      }, Number(node.dataset.delay) || 0);
    };
    const reveal = (node) => {
      node.classList.add('is-in');
      node.querySelectorAll('.bzs-count, .bzs-wnum').forEach(countUp);
    };
    const targets = sheet.querySelectorAll('.bzs-reveal');
    if ('IntersectionObserver' in window && !reduce.matches) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          io.unobserve(entry.target);
        });
      }, { threshold: 0.25 });
      targets.forEach((node) => io.observe(node));
    } else targets.forEach(reveal);
  };

  const start = () => document.querySelectorAll('[data-bz-spec]').forEach(build);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
